import React, {useEffect, useState} from "react";
import {useObserver} from "mobx-react-lite";
import {Col, Row, Form, Input} from "antd";
import {BankOutlined} from "@ant-design/icons";
import MaskedInput from "antd-mask-input";
import {useFormik, FormikProvider, getIn} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import * as Sentry from "@sentry/react";

import {SingleMarkerMap} from "../../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import {addSubCompanyPropType} from "../addSubCompany/addSubCompanyPropTypes";
import {addSubCompanyValidation} from "../addSubCompany/addSubCompanyValidation";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import TagInput from "../../_defaultComponents/TagInput/TagInput";
import {Switch} from "antd";
export const AddSubCompany = ({companyId}) => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation();
    const [companyCode, setCompanyCode] = useState();
    const [companyCityCode, setCompanyCityCode] = useState();
    const [isTestUser, setIsTestUser] = useState(false);
    const [tags, setTags] = useState([]);

    /*TODO: Form component will be used in UpdateSubCompany and AddSubCompany pages. 
    Code refactoring needed for blocking duplicate usage.
    */

    const formik = useFormik({
        initialValues: {
            address: "",
            authorizedEmail: "",
            authorizedPhone: "",
            authorizedPerson: "",
            disabled: false,
            name: "",
            location: {
                longitude: 29.14,
                latitude: 41.09
            },
            uniqueKey: "",
            subCompanyCode: "",
            companyId
        },
        validationSchema: addSubCompanyValidation,
        enableReinitialize: true,
        onSubmit: async (values: addSubCompanyPropType) => {
            values.authorizedPhone = values.authorizedPhone.replace(/[-,(,)]/g, "");
            values.subCompanyCode = `${companyCode}-${("00" + companyCityCode).slice(-2)}-${
                values.subCompanyCode
            }`;
            values.isTestUser = isTestUser;
            await handleCompanySubmit(values);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    const handleCompanySubmit = async (values: addSubCompanyPropType) => {
        const result = await stores.companyStore.createSubCompany(values);
        if (result) {
            history.push("/companies/" + companyId);
        }
    };

    useEffect(() => {
        stores.companyStore
            .generateSubcompanyCode({
                companyId,
                subCompanyLocation: values.location
            })
            .then(res => {
                setCompanyCode(res.companyCode);
                setCompanyCityCode(res.cityCode);
                setFieldValue("subCompanyCode", ("0000" + res.code).slice(-4));
            })
            .catch(e => Sentry.captureException(e));
    }, [values.location.longitude, values.location.latitude]);

    useEffect(() => {
        setFieldValue("allowedOrderProviders", tags);
    }, [tags]);

    return useObserver(() => (
        <Col data-testid="addSubCompanyScreen">
            <Form
                autoComplete="off"
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                onFinish={handleSubmit}
                data-testid="addSubCompanyForm"
            >
                <ErrorFocus formik={formik} />
                <FormikProvider value={formik}>
                    <Form.Item
                        label={`${t("fields.companyName", {
                            ns: "common"
                        })} / ${t("fields.branchName", {ns: "common"})}`}
                        validateStatus={
                            getIn(errors, `name`) && getIn(touched, `name`) ? "error" : "success"
                        }
                        help={
                            getIn(errors, `name`) && getIn(touched, `name`)
                                ? getIn(errors, `name`)
                                : null
                        }
                    >
                        <Input
                            name={"name"}
                            onChange={handleChange}
                            value={values.name}
                            data-testid="nameInpuy"
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.authorizedPerson", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `authorizedPerson`) && getIn(touched, `authorizedPerson`)
                                ? getIn(errors, `authorizedPerson`)
                                : null
                        }
                    >
                        <Input
                            name={"authorizedPerson"}
                            data-testid="authorizedPerson"
                            value={values.authorizedPerson}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Row type="flex" justify="space-between">
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedPhone", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedPhone`) &&
                                    getIn(touched, `authorizedPhone`)
                                        ? getIn(errors, `authorizedPhone`)
                                        : null
                                }
                            >
                                <MaskedInput
                                    placeholder="0(555)-555-5555"
                                    mask="1(111)-111-1111"
                                    name={"authorizedPhone"}
                                    data-testid="authorizedPhone"
                                    value={values.authorizedPhone}
                                    onChange={handleChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span="11">
                            <Form.Item
                                label={t("fields.authorizedEmail", {ns: "common"})}
                                validateStatus={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    getIn(errors, `authorizedEmail`) &&
                                    getIn(touched, `authorizedEmail`)
                                        ? getIn(errors, `authorizedEmail`)
                                        : null
                                }
                            >
                                <Input
                                    name={"authorizedEmail"}
                                    data-testid="authorizedEmail"
                                    onChange={handleChange}
                                    value={values.authorizedEmail}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        label={t("fields.address", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `address`) && getIn(touched, `address`)
                                ? getIn(errors, `address`)
                                : null
                        }
                    >
                        <Input.TextArea
                            name={`address`}
                            data-testid="address"
                            value={values.address}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Form.Item label={t("fields.uniqueKey", {ns: "common"})}>
                        <Input
                            name={"uniqueKey"}
                            data-testid="uniqueKey"
                            onChange={handleChange}
                            value={values.uniqueKey}
                        />
                    </Form.Item>
                    <Form.Item label={t("fields.testUser", {ns: "common"})}>
                        <Switch onChange={setIsTestUser} checked={isTestUser} />
                    </Form.Item>
                    <Form.Item
                        label={t(`integrations.commonFields.allowedOrderProviders`, {
                            ns: "integrations"
                        })}
                    >
                        <TagInput
                            tags={tags}
                            setTags={setTags}
                            addTitle={t(`integrations.commonFields.addAllowedOrderProvider`, {
                                ns: "integrations"
                            })}
                            addPlaceHolderText={t(
                                `integrations.commonFields.allowedOrderProvidersPlaceholder`,
                                {
                                    ns: "integrations"
                                }
                            )}
                        ></TagInput>
                    </Form.Item>
                    <Form.Item
                        label={t("fields.location", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `location`) && getIn(touched, `location`)
                                ? getIn(errors, `location`)
                                : null
                        }
                    >
                        <Row type="flex" gutter={16} justify="center">
                            <Col data-testid="latitudeText">{`${t("fields.latitude", {
                                ns: "common"
                            })}: ${values.location.latitude.toFixed(5)}`}</Col>
                            <Col data-testid="longitudeText">{`${t("fields.longitude", {
                                ns: "common"
                            })}: ${values.location.longitude.toFixed(5)}`}</Col>
                        </Row>
                        <SingleMarkerMap
                            center={{
                                lat: values.location.latitude,
                                lng: values.location.longitude
                            }}
                            onClick={({lat, lng}) => {
                                const c = {
                                    longitude: lng,
                                    latitude: lat
                                };
                                setFieldValue(`location`, c);
                            }}
                            addExternalInput
                            icon={<BankOutlined />}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.subCompanyCode", {ns: "common"})}
                        validateStatus={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? "error"
                                : "success"
                        }
                        help={
                            getIn(errors, `subCompanyCode`) && getIn(touched, `subCompanyCode`)
                                ? getIn(errors, `subCompanyCode`)
                                : null
                        }
                    >
                        <Row>
                            <Input
                                disabled
                                value={`${companyCode}`}
                                style={{borderRightWidth: 0, width: 60}}
                            />
                            <Input
                                disabled
                                value={`${companyCityCode}`}
                                style={{borderRightWidth: 0, width: 40}}
                            />
                            <Input
                                name={"subCompanyCode"}
                                data-testid="subCompanyCode"
                                value={values.subCompanyCode}
                                onChange={handleChange}
                                style={{borderLeftWidth: 0, flex: 1}}
                            />
                        </Row>
                    </Form.Item>
                </FormikProvider>
                <Row type="flex">
                    <CustomButton
                        type="primary"
                        htmlType="submit"
                        data-testid="submitButton"
                        disabled={isSubmitting}
                    >
                        {t("actions.save", {ns: "common"})}
                    </CustomButton>
                </Row>
            </Form>
        </Col>
    ));
};
